import {
  getUsers, getUser, banUser, unbanUser, verifyEmail, addCredits,
  createUser, updateUser, setUserPassword,
} from '../admin-api';
import type { AdminUser, AdminUserDetail, AdminUserPatch } from '../admin-api';
import { esc, adminTable, pagination, searchBar, badge, showConfirm, showPrompt, formField, adminSelect, formatDate } from '../admin-ui';
import { cmsErrorText } from './admin-cms';
import { note, scopedHelpers } from '../admin-helpers';

const ROLE_OPTIONS = ['PLAYER', 'ADMIN', 'KYC_ADMIN'].map((v) => ({ value: v, label: v }));
const KYC_OPTIONS = ['NONE', 'PENDING', 'APPROVED', 'REJECTED'].map((v) => ({ value: v, label: v }));
const YES_NO = [{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }];

/**
 * The editable profile, in form order (2026-10-14). `kind` picks the control;
 * keys are the `PATCH /admin/game-users/:id` body. Text fields send '' to
 * clear, which the server stores as NULL.
 */
export const USER_FIELDS: Array<{ key: keyof AdminUserPatch; label: string; kind: 'text' | 'email' | 'role' | 'kyc' | 'bool' }> = [
  { key: 'email', label: 'Email', kind: 'email' },
  { key: 'username', label: 'Username', kind: 'text' },
  { key: 'role', label: 'Role', kind: 'role' },
  { key: 'emailVerified', label: 'Email verified', kind: 'bool' },
  { key: 'kycStatus', label: 'KYC status', kind: 'kyc' },
  { key: 'firstName', label: 'First name', kind: 'text' },
  { key: 'lastName', label: 'Last name', kind: 'text' },
  { key: 'locale', label: 'Locale (en_GB)', kind: 'text' },
  { key: 'currency', label: 'Currency (EUR)', kind: 'text' },
  { key: 'billingLine1', label: 'Billing address', kind: 'text' },
  { key: 'billingLine2', label: 'Billing address 2', kind: 'text' },
  { key: 'billingCity', label: 'Billing city', kind: 'text' },
  { key: 'billingZip', label: 'Billing postcode', kind: 'text' },
  { key: 'billingCountry', label: 'Billing country (EE)', kind: 'text' },
];

function fieldMarkup(f: (typeof USER_FIELDS)[number], u: AdminUserDetail): string {
  const id = `adm-uf-${f.key}`;
  const raw = (u as unknown as Record<string, unknown>)[f.key];
  if (f.kind === 'role') return adminSelect(f.label, id, ROLE_OPTIONS, String(raw ?? 'PLAYER'));
  if (f.kind === 'kyc') return adminSelect(f.label, id, KYC_OPTIONS, String(raw ?? 'NONE'));
  if (f.kind === 'bool') return adminSelect(f.label, id, YES_NO, String(Boolean(raw)));
  return formField(f.label, id, f.kind, raw == null ? '' : String(raw));
}

/**
 * Only what the admin changed. Sending every field would rewrite values
 * nobody touched — and the email field round-tripping case would trip the
 * "already taken" check on some providers' casing.
 */
export function userPatch(u: AdminUserDetail, read: (id: string) => string): AdminUserPatch {
  const out: Record<string, unknown> = {};
  for (const f of USER_FIELDS) {
    const before = (u as unknown as Record<string, unknown>)[f.key];
    const value = read(`adm-uf-${f.key}`);
    if (f.kind === 'bool') {
      const next = value === 'true';
      if (next !== Boolean(before)) out[f.key] = next;
      continue;
    }
    let next = value.trim();
    if (f.key === 'currency' || f.key === 'billingCountry') next = next.toUpperCase();
    if (next !== String(before ?? '')) out[f.key] = next;
  }
  return out as AdminUserPatch;
}

function roleBadge(role: string): string {
  return badge(role, role === 'ADMIN' ? 'blue' : 'gray');
}

function kycBadge(status: string): string {
  const c: Record<string, 'green' | 'red' | 'yellow' | 'gray'> = {
    APPROVED: 'green', PENDING: 'yellow', REJECTED: 'red', NONE: 'gray',
  };
  return badge(status, c[status] ?? 'gray');
}

function statusBadge(banned: boolean): string {
  return banned ? badge('Banned', 'red') : badge('Active', 'green');
}

export async function mountUsers(container: HTMLElement): Promise<() => void> {
  let alive = true;
  let users: AdminUser[] = [];
  let page = 1;
  let totalPages = 1;
  let search = '';
  let detail: AdminUserDetail | null = null;
  let debounceTimer: ReturnType<typeof setTimeout> | undefined;

  const { $, read, say, run } = scopedHelpers(container);

  async function loadList(): Promise<void> {
    if (!alive) return;
    try {
      // `per_page`, not `limit`: core's ListUsersQueryDto whitelists page,
      // per_page, search and status, and the global ValidationPipe runs with
      // forbidNonWhitelisted, so an unknown key is a 400 rather than an
      // ignored one.
      const res = await getUsers({ page, per_page: 25, search: search || undefined });
      users = res.items;
      totalPages = Math.ceil(res.total / res.pageSize) || 1;
    } catch {
      container.innerHTML = '<p class="text-red-400 p-4">Failed to load users.</p>';
      return;
    }
    renderList();
  }

  function renderList(): void {
    detail = null;
    const tableRows = users.map(u => [
      esc(u.username ?? '-'),
      esc(u.email),
      roleBadge(u.role),
      kycBadge(u.kycStatus),
      statusBadge(u.banned),
      `<button class="btn btn-stone min-h-11 px-3 text-xs" data-view-user="${esc(u.id)}">Edit</button>`,
    ]);

    container.innerHTML = `
      <div class="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h2 class="font-heading text-gold text-lg">Players</h2>
        <button id="adm-user-new" class="btn btn-gold min-h-11 px-4 text-sm">New player</button>
      </div>
      <div id="adm-user-create" class="hidden frame-pewter p-4 mb-4">
        <h3 class="font-heading text-gold text-sm mb-3">New player</h3>
        <div class="grid gap-x-3 sm:grid-cols-2">
          ${formField('Email', 'adm-new-email', 'email', '', { required: true })}
          ${formField('Username (optional)', 'adm-new-username', 'text')}
          ${formField('Password (6+ characters)', 'adm-new-password', 'password', '', { required: true })}
          ${adminSelect('Role', 'adm-new-role', ROLE_OPTIONS, 'PLAYER')}
          ${adminSelect('Email verified', 'adm-new-verified', YES_NO, 'true')}
          ${adminSelect('KYC status', 'adm-new-kyc', KYC_OPTIONS, 'NONE')}
        </div>
        <button id="adm-new-save" class="btn btn-gold min-h-11 px-4 text-sm">Create</button>
        ${note('adm-new-msg')}
      </div>
      <div class="mb-4">${searchBar('Search players...', 'adm-user-search')}</div>
      ${adminTable(['Username', 'Email', 'Role', 'KYC', 'Status', 'Actions'], tableRows)}
      ${pagination(page, totalPages)}
    `;
    wireList();
  }

  function wireList(): void {
    const input = $<HTMLInputElement>('#adm-user-search');
    if (input) {
      input.value = search;
      input.addEventListener('input', () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          search = input.value.trim();
          page = 1;
          void loadList();
        }, 300);
      });
    }
    container.querySelectorAll<HTMLButtonElement>('[data-page]').forEach(btn => {
      btn.addEventListener('click', () => {
        page = Number(btn.dataset.page);
        void loadList();
      });
    });
    container.querySelectorAll<HTMLButtonElement>('[data-view-user]').forEach(btn => {
      btn.addEventListener('click', () => void openUser(btn.dataset.viewUser!));
    });

    $('#adm-user-new')?.addEventListener('click', () => {
      $('#adm-user-create')?.classList.toggle('hidden');
      $<HTMLInputElement>('#adm-new-email')?.focus();
    });

    $('#adm-new-save')?.addEventListener('click', () => {
      void run($<HTMLButtonElement>('#adm-new-save'), 'adm-new-msg', async () => {
        const email = read('adm-new-email').trim();
        const password = read('adm-new-password');
        if (!email) throw new Error('Enter an email');
        if (password.length < 6) throw new Error('Password needs at least 6 characters');
        const username = read('adm-new-username').trim();
        const created = await createUser({
          email,
          password,
          ...(username ? { username } : {}),
          role: read('adm-new-role'),
          emailVerified: read('adm-new-verified') === 'true',
          kycStatus: read('adm-new-kyc'),
        });
        // Straight into the new player's page: the next thing is usually to
        // fill in their name or grant them something.
        await openUser(created.id);
        say('adm-user-save-msg', `Created ${created.username ?? created.email}.`);
        return 'Created.';
      });
    });
  }

  async function openUser(id: string): Promise<void> {
    try {
      detail = await getUser(id);
      renderDetail();
    } catch {
      container.innerHTML = '<p class="text-red-400 p-4">Failed to load user.</p>';
    }
  }

  function renderDetail(): void {
    if (!detail) return;
    const u = detail;
    container.innerHTML = `
      <button id="adm-user-back" class="btn btn-stone min-h-11 px-4 text-sm mb-4">&larr; Back</button>
      <div class="frame-pewter p-4 mb-4">
        <h3 class="font-heading text-gold text-lg mb-3">${esc(u.username ?? u.email)}</h3>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-[13px]">
          <div><span class="text-muted">Role:</span> ${roleBadge(u.role)}</div>
          <div><span class="text-muted">KYC:</span> ${kycBadge(u.kycStatus)}</div>
          <div><span class="text-muted">Status:</span> ${statusBadge(u.banned)}</div>
          <div><span class="text-muted">Joined:</span> <span class="text-text">${formatDate(u.createdAt)}</span></div>
          <div><span class="text-muted">Wax:</span> <span class="text-text">${u.softBalance}</span></div>
          <div><span class="text-muted">Ember:</span> <span class="text-text">${u.premiumBalance}</span></div>
          ${u.banned && u.banReason ? `<div class="sm:col-span-2"><span class="text-muted">Ban reason:</span> <span class="text-red-400">${esc(u.banReason)}</span></div>` : ''}
        </div>
      </div>

      <div class="frame-pewter p-4 mb-4">
        <h3 class="font-heading text-gold text-sm mb-3">Profile</h3>
        <div class="grid gap-x-3 sm:grid-cols-2">
          ${USER_FIELDS.map((f) => fieldMarkup(f, u)).join('')}
        </div>
        <button id="adm-user-save" class="btn btn-gold min-h-11 px-4 text-sm">Save changes</button>
        ${note('adm-user-save-msg')}
      </div>

      <div class="frame-pewter p-4 mb-4">
        <h3 class="font-heading text-gold text-sm mb-1">Password</h3>
        <p class="text-[11px] text-muted mb-3">Sets a new password and signs the player out on every device.</p>
        ${formField('New password', 'adm-user-password', 'password')}
        <button id="adm-user-password-save" class="btn btn-stone min-h-11 px-4 text-sm">Set password</button>
        ${note('adm-user-password-msg')}
      </div>

      <div class="flex flex-wrap gap-2">
        ${u.banned
          ? `<button id="adm-user-unban" class="btn btn-gold min-h-11 px-4 text-sm">Unban</button>`
          : `<button id="adm-user-ban" class="btn btn-stone min-h-11 px-4 text-sm text-red-400">Ban</button>`}
        ${!u.emailVerified ? `<button id="adm-user-verify-email" class="btn btn-stone min-h-11 px-4 text-sm">Verify Email</button>` : ''}
        <button id="adm-user-credits" class="btn btn-stone min-h-11 px-4 text-sm">Add Credits</button>
        ${note('adm-user-action-msg')}
      </div>

      <div id="adm-user-credits-form" class="hidden frame-pewter p-4 mt-4">
        ${formField('Amount', 'adm-credits-amount', 'number')}
        ${adminSelect('Currency', 'adm-credits-currency', [{ value: 'SOFT', label: 'Wax (SOFT)' }, { value: 'PREMIUM', label: 'Ember (PREMIUM)' }], 'SOFT')}
        <button id="adm-credits-save" class="btn btn-gold min-h-11 px-4 text-sm mt-2">Grant</button>
        ${note('adm-credits-msg')}
      </div>
    `;
    wireDetail();
  }

  function wireDetail(): void {
    if (!detail) return;
    const userId = detail.id;
    const refresh = async () => { detail = await getUser(userId); renderDetail(); };

    $('#adm-user-back')?.addEventListener('click', () => void loadList());

    $('#adm-user-save')?.addEventListener('click', () => {
      void run($<HTMLButtonElement>('#adm-user-save'), 'adm-user-save-msg', async () => {
        const patch = userPatch(detail!, read);
        if (!Object.keys(patch).length) return 'No changes.';
        await updateUser(userId, patch);
        await refresh();
        say('adm-user-save-msg', 'Saved.');
        return 'Saved.';
      });
    });

    $('#adm-user-password-save')?.addEventListener('click', () => {
      void run($<HTMLButtonElement>('#adm-user-password-save'), 'adm-user-password-msg', async () => {
        const pw = read('adm-user-password');
        if (pw.length < 6) throw new Error('Password needs at least 6 characters');
        await setUserPassword(userId, pw);
        const input = $<HTMLInputElement>('#adm-user-password');
        if (input) input.value = '';
        return 'Password set.';
      });
    });

    // Core records the reason in the player's event log (2026-10-19); it used
    // to be the hardcoded "Admin action", so no ban said why it happened.
    $('#adm-user-ban')?.addEventListener('click', () => {
      showPrompt({
        title: 'Ban player',
        message: `Signs ${esc(detail!.username ?? detail!.email)} out everywhere and blocks sign-in.`,
        label: 'Reason',
        placeholder: 'e.g. chargeback abuse',
        confirmLabel: 'Ban',
        minLength: 3,
        maxLength: 1000,
      }, async (reason) => {
        try {
          await banUser(userId, reason);
        } catch (err) {
          say('adm-user-action-msg', cmsErrorText(err), true);
          return;
        }
        // The ban is committed; a failed re-read must not report it as failed.
        try {
          await refresh();
        } catch {
          say('adm-user-action-msg', 'Player banned. Reload to see the updated profile.');
        }
      });
    });

    $('#adm-user-unban')?.addEventListener('click', () => {
      showConfirm('Unban this user?', async () => {
        await unbanUser(userId);
        await refresh();
      });
    });

    $('#adm-user-verify-email')?.addEventListener('click', () => {
      showConfirm('Mark email as verified?', async () => {
        await verifyEmail(userId);
        await refresh();
      });
    });

    $('#adm-user-credits')?.addEventListener('click', () => {
      $('#adm-user-credits-form')?.classList.toggle('hidden');
    });

    $('#adm-credits-save')?.addEventListener('click', () => {
      void run($<HTMLButtonElement>('#adm-credits-save'), 'adm-credits-msg', async () => {
        const amount = Number(read('adm-credits-amount'));
        if (!Number.isInteger(amount) || amount <= 0) throw new Error('Enter a whole amount above 0');
        await addCredits(userId, { amount, currency: read('adm-credits-currency') || 'SOFT' });
        await refresh();
        return 'Granted.';
      });
    });
  }

  await loadList();
  return () => { alive = false; clearTimeout(debounceTimer); };
}
